import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ShieldCheck,
  CheckCircle2,
  XCircle,
  ArrowRight,
  Scale,
  BookOpen,
  Building2,
} from "lucide-react";
import { Disclaimer } from "@/components/site/Disclaimer";
import { RelatedServices } from "@/components/site/RelatedServices";

export const Route = createFileRoute("/compliance-services")({
  head: () => ({
    meta: [
      {
        title:
          "Compliance & Services — What we do (and do not do) | Nkadimeng Danny",
      },
      {
        name: "description",
        content:
          "We are NOT a firm of attorneys and we do not represent clients in court. We do provide legal information, legal advice on non-reserved matters and legal opinions. Read our full scope of legal-support, compliance, document-preparation and education services.",
      },

      {
        property: "og:title",
        content: "Compliance & Services — Scope of what we offer",
      },
      {
        property: "og:description",
        content:
          "Transparent statement of our legal-support and compliance services, our limitations under the Legal Practice Act, and how we refer matters that need an admitted attorney.",
      },
      {
        property: "og:url",
        content: "https://nkadimengdannylcs.com/compliance-services",
      },
    ],
    links: [
      {
        rel: "canonical",
        href: "https://nkadimengdannylcs.com/compliance-services",
      },
    ],
  }),
  component: ComplianceServicesPage,
});

const DO_OFFER = [
  "Legal information and legal advice on non-reserved matters",
  "Preparing legal opinions",
  "Drafting templates, contracts and statutory documents",
  "POPIA, BCEA, OHSA, B-BBEE and CIPC compliance consulting",
  "CCMA Form 7.11 preparation, condonation analysis and document bundles",
  "Company secretarial: MOI, constitution, registers, minutes",
  "AI Labour-Law Analyser (human-reviewed before delivery)",
  "Education: NDLCS Academy, UNISA LLB tutoring, Legal-Writing Academy",
  "AI CV Builder, interview coaching and Z83 application packs",
  "Corporate training, awareness sessions and policy workshops",
];

const DO_NOT_OFFER = [
  "Court representation of any kind",
  "Advice or work on reserved matters requiring an admitted practitioner",
  "Conveyancing, notarial or estate-administration work",
  "Operating a trust account or issuing Fidelity-Fund-backed receipts",
  "Negotiation with third parties as a legal representative",
  "Drafting or filing court process on your behalf",
  "Holding ourselves out as attorneys or advocates",
];

function ComplianceServicesPage() {
  return (
    <div className="container-narrow space-y-10 py-12">
      {/* Hero */}
      <header className="space-y-4">
        <p className="text-xs uppercase tracking-[0.3em] text-gold">
          Compliance &amp; transparency
        </p>
        <h1 className="font-display text-3xl text-navy md:text-4xl">
          What we do — and what we do not do
        </h1>
        <p className="max-w-3xl text-foreground/80">
          This page is maintained by Nkadimeng Danny Legal and Compliance
          Support to set out, in plain language, the scope of our services, our
          position under the Legal Practice Act 28 of 2014, and how we handle
          matters that fall outside our scope.
        </p>
      </header>

      <Disclaimer variant="highlight" />

      {/* Position statement */}
      <section className="rounded-2xl border border-gold/40 bg-card p-6 shadow-soft">
        <div className="flex items-start gap-4">
          <div className="grid h-11 w-11 shrink-0 place-items-center rounded-md bg-navy text-gold">
            <Scale className="h-5 w-5" />
          </div>
          <div className="space-y-3">
            <h2 className="font-display text-xl text-navy">
              Our position — clearly stated
            </h2>
            <p className="text-sm leading-relaxed text-foreground/85">
              Nkadimeng Danny Legal and Compliance Support is a registered
              South African legal-support and compliance practice founded by an
              LLB graduate. We are <strong>NOT a firm of attorneys</strong>, we
              are not regulated by the Legal Practice Council, and we do not
              hold a Fidelity Fund Certificate.
            </p>
            <ul className="space-y-2 text-sm text-foreground/85">
              <li className="flex items-start gap-2">
                <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
                <span>
                  We <strong>do not represent clients</strong> in any court,
                  tribunal, CCMA hearing or arbitration.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-700" />
                <span>
                  We <strong>do provide legal information and legal advice on
                  non-reserved matters</strong>, and we prepare legal opinions.
                  We do not advise on or undertake matters reserved by the
                  Legal Practice Act 28 of 2014.
                </span>
              </li>

              <li className="flex items-start gap-2">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-700" />
                <span>
                  Where a matter requires representation or a formal legal
                  opinion, we will <strong>refer you</strong> to a suitably
                  qualified admitted attorney — at no commission to us.
                </span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Two columns: do / do not */}
      <section className="grid gap-5 md:grid-cols-2">
        <article className="rounded-2xl border border-border bg-card p-6 shadow-soft">
          <div className="mb-3 flex items-center gap-2 text-navy">
            <CheckCircle2 className="h-5 w-5 text-green-700" />
            <h2 className="font-display text-lg">What we DO offer</h2>
          </div>
          <ul className="space-y-2 text-sm text-foreground/85">
            {DO_OFFER.map((item) => (
              <li key={item} className="flex items-start gap-2">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-700" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </article>

        <article className="rounded-2xl border border-border bg-card p-6 shadow-soft">
          <div className="mb-3 flex items-center gap-2 text-navy">
            <XCircle className="h-5 w-5 text-red-600" />
            <h2 className="font-display text-lg">
              What we do NOT offer
            </h2>
          </div>
          <ul className="space-y-2 text-sm text-foreground/85">
            {DO_NOT_OFFER.map((item) => (
              <li key={item} className="flex items-start gap-2">
                <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </article>
      </section>

      {/* How we deliver */}
      <section className="rounded-2xl border border-border bg-card p-6 shadow-soft">
        <div className="mb-4 flex items-center gap-2 text-navy">
          <ShieldCheck className="h-5 w-5 text-gold" />
          <h2 className="font-display text-xl">How we engage clients</h2>
        </div>
        <ol className="grid gap-3 text-sm text-foreground/85 md:grid-cols-2">
          {[
            "Written scope of work issued before any work begins",
            "Plain-language reminder that we are NOT attorneys",
            "Transparent quotation paid securely via PayFast",
            "POPIA-compliant upload of supporting documents",
            "Delivery through your secure client portal with audit trail",
            "Referral to an admitted attorney where representation is required",
          ].map((step, i) => (
            <li
              key={step}
              className="flex items-start gap-3 rounded-md border border-border bg-secondary/40 p-3"
            >
              <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-navy text-xs font-semibold text-gold">
                {i + 1}
              </span>
              <span>{step}</span>
            </li>
          ))}
        </ol>
      </section>

      {/* Confidential records */}
      <section className="rounded-2xl border border-border bg-secondary/40 p-6">
        <div className="flex items-start gap-3">
          <Building2 className="mt-1 h-5 w-5 shrink-0 text-gold" />
          <div className="space-y-2 text-sm text-foreground/85">
            <h2 className="font-display text-lg text-navy">
              Confidential corporate records
            </h2>
            <p>
              Our internal corporate assets — MOI, Constitution, Company
              Profile, Management Plan, B-BBEE Policy, Compliance Manual,
              Service Catalogue and Scope-of-Work Template — are{" "}
              <strong>confidential</strong> and are accessible only to
              authorised admin users of the firm.
            </p>
            <p className="text-muted-foreground">
              These records are versioned, brand-verified and reviewed
              annually. They are not distributed to clients or the public.
            </p>
          </div>
        </div>
      </section>

      {/* Compliance & referrals */}
      <section className="grid gap-5 md:grid-cols-2">
        <article className="rounded-2xl border border-border bg-card p-6 shadow-soft">
          <div className="mb-2 flex items-center gap-2 text-navy">
            <BookOpen className="h-5 w-5 text-gold" />
            <h3 className="font-display text-lg">Legislation we respect</h3>
          </div>
          <ul className="space-y-1.5 text-sm text-foreground/85">
            <li>• Legal Practice Act 28 of 2014</li>
            <li>• Companies Act 71 of 2008</li>
            <li>• Protection of Personal Information Act 4 of 2013</li>
            <li>• Basic Conditions of Employment Act 75 of 1997</li>
            <li>• Consumer Protection Act 68 of 2008</li>
            <li>• Financial Intelligence Centre Act 38 of 2001</li>
          </ul>
        </article>
        <article className="rounded-2xl border border-border bg-card p-6 shadow-soft">
          <div className="mb-2 flex items-center gap-2 text-navy">
            <Scale className="h-5 w-5 text-gold" />
            <h3 className="font-display text-lg">When we refer you on</h3>
          </div>
          <p className="text-sm text-foreground/85">
            If your matter requires court appearance, litigation,
            conveyancing, notarial work, or any service reserved by the Legal
            Practice Act, we will refer you to an admitted attorney from our
            referral network. We do not accept commissions for these
            referrals.
          </p>
        </article>
      </section>

      {/* CTA */}
      <section className="rounded-2xl border border-gold/40 bg-navy p-6 text-white shadow-soft">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-gold">
              Ready to engage us?
            </p>
            <h3 className="mt-1 font-display text-xl">
              Request a written scope of work
            </h3>
            <p className="mt-1 text-sm text-white/80">
              Every engagement begins with a transparent scope and a PayFast
              quotation. No payment, no work.
            </p>
          </div>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 rounded-md bg-gold px-4 py-2 text-sm font-semibold text-navy hover:bg-gold/90"
          >
            Contact us <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <p className="text-xs text-muted-foreground">
        This page is maintained by Nkadimeng Danny Legal and Compliance Support
        and is reviewed annually. For the binding legal terms see our{" "}
        <Link to="/disclaimer" className="underline">
          full disclaimer
        </Link>
        ,{" "}
        <Link to="/terms" className="underline">
          terms of service
        </Link>{" "}
        and{" "}
        <Link to="/privacy" className="underline">
          privacy policy
        </Link>
        .
      </p>

      <RelatedServices
        exclude={["/compliance-services"]}
        limit={3}
        className="!px-0 !py-4"
      />
    </div>
  );
}
